import { useState } from "react";
import { Loader2 } from "lucide-react";
import { trpc } from "@/lib/trpc";
import { usePermissao } from "@/lib/use-permissao";
import { isOverdue } from "@/lib/dashboard-lab";
import { STATUS_PHASES } from "@/components/prosthesis/prosthesis-meta";
import type { IncomingOrderRow } from "@/components/production/production-types";
import { IncomingOrdersMobileList } from "@/components/production/IncomingOrdersMobileList";
import { ProductionOrderDetailDialog } from "@/components/production/ProductionOrderDetailDialog";
import { LabDeadlineSummary } from "@/components/dashboard2/LabDeadlineSummary";
import { SemPermissao } from "./SemPermissao";
import type { PrazoDoLaboratorio } from "@bookdental/schemas";

const producaoStatuses = STATUS_PHASES.find((p) => p.key === "producao")!.statuses;

export type FiltroDoLab = "pendentes" | "producao" | "retrabalho" | "atrasados" | "resumo";

const VAZIO: Record<Exclude<FiltroDoLab, "resumo">, string> = {
  pendentes: "Nenhum pedido aguardando confirmação.",
  producao: "Nada em produção agora.",
  retrabalho: "Nenhum pedido em retrabalho.",
  atrasados: "Nenhum trabalho atrasado.",
};

/**
 * Pedidos do laboratório em foco. Os três filtros usam as MESMAS contas dos cartões da prévia
 * (e do Início do laboratório), pra lista bater com o número que a pessoa clicou. O pedido abre
 * no diálogo de sempre, com aceitar/recusar. "Resumo" é o resumo por prazo do Início.
 */
export function PainelPedidosDoLab({ filtro, prazo }: { filtro: FiltroDoLab; prazo: PrazoDoLaboratorio }) {
  const podeVer = usePermissao("production.view");
  const pedidos = trpc.prosthesis.incoming.list.useQuery(undefined, { enabled: podeVer });
  const [abertoId, setAbertoId] = useState<string | null>(null);

  if (!podeVer) return <SemPermissao area="a produção" />;
  const todos = (pedidos.data ?? []) as IncomingOrderRow[];

  if (filtro === "resumo") {
    return <LabDeadlineSummary orders={todos} loading={pedidos.isLoading} prazo={prazo} semLinks />;
  }

  const lista = todos.filter((o) =>
    filtro === "pendentes"
      ? o.status === "AGUARDANDO_CONFIRMACAO"
      : filtro === "producao"
        ? producaoStatuses.includes(o.status)
        : filtro === "retrabalho"
          ? o.services.some((s) => s.hasOpenRework)
          : isOverdue(o, prazo),
  );
  const aberto = todos.find((o) => o.id === abertoId) ?? null;

  return (
    <>
      {pedidos.isLoading ? (
        <div className="flex justify-center py-8">
          <Loader2 className="h-5 w-5 animate-spin text-brand-600" />
        </div>
      ) : lista.length === 0 ? (
        <p className="py-8 text-center text-sm text-zinc-500 dark:text-muted-foreground">{VAZIO[filtro]}</p>
      ) : (
        <IncomingOrdersMobileList orders={lista} onViewDetail={(o) => setAbertoId(o.id)} />
      )}
      <ProductionOrderDetailDialog order={aberto} onClose={() => setAbertoId(null)} />
    </>
  );
}
